import { Collection, getFirebase } from 'firestorter';
import moment from 'moment';

import CollectionName from '../utils/collections';
import { ChannelBuckets } from '../utils/channelBuckets';
import { getServerTimestamp } from '../utils/time';
import { ALL_CLIENTS } from '../utils/coachPod';
import { stripMarkdownLinks } from '../utils/dataProcessing';

import ChatMessage from './ChatMessage';
import ChatMessageRequest, { CHAT_MESSAGE_REQUEST_STATUS } from './ChatMessageRequest';
import BaseDocument from './BaseDocument';

const ChannelType = {
  USER: 'USER',
  GROUP: 'GROUP',
  ADMIN_COACHING: 'ADMIN_COACHING',
};

const ChannelOrderBy = {
  ASC: 'asc',
  DESC: 'desc',
};

const ChannelClaimedFrom = {
  CHAT: 'CHAT',
  CHECKIN: 'CHECK_IN',
};

const CHANNEL_LIST_PAGE_SIZE = 100;
// A page is documents, not visible messages: Admin Coaching pages also hold the replies the feed hides.
const CHANNEL_MESSAGE_PAGE_SIZE = 30;
// First slice of a thread. Long threads grow this on demand instead of loading every reply at once.
const THREAD_REPLY_PAGE_SIZE = 30;
/*
  Watched by the thread inbox, independent of the message window so a reply stays announceable. Sized
  for a browsable list rather than a bare count: at 20 replies a single chatty thread could fill the
  window and leave the inbox showing three rows.
*/
const UNSEEN_THREAD_REPLY_LIMIT = 50;
/*
  Hard ceiling on the unread query, which is otherwise bounded by time rather than by count. It is
  not the operating bound and should never bind — when it does, the inbox says so on screen, and
  that notice is the signal to add a watermark advance beyond Mark all read.
*/
const UNREAD_THREAD_SEATBELT = 500;
const CHANNEL_CLAIM_VALIDITY_HOURS = 1;

/**
 * Adds a podId filter to a query, unless no pod or "All Clients" is selected
 * @param {Object} query
 * @param {string} selectedPodId
 * @returns {Object}
 */
const applyPodFilter = (query, selectedPodId) => (
  selectedPodId && selectedPodId !== ALL_CLIENTS
    ? query.where('podId', '==', selectedPodId)
    : query
);

/**
 * Class representing a Chat Channel.
 *
 * @class ChatChannel
 * @extends BaseDocument
 */
class ChatChannel extends BaseDocument {
  constructor(id, opts) {
    super(`${CollectionName.CHAT_CHANNEL}/${id}`, opts);
  }

  /**
   * Get channel's name.
   * @return {string}
   */
  get name() {
    return this.data.name;
  }

  /**
   * Get channel's search name.
   * @return {string}
   */
  get searchName() {
    return this.data.searchName || this.name.toLowerCase();
  }

  /**
   * Get profile image url of the channel.
   * @return {string}
   */
  get profileImage() {
    return this.data.profileImage;
  }

  /**
   * Get the time of the channel creation.
   * @return {Date|null}
   */
  get createdAt() {
    return this.data.createdAt?.toDate() || null;
  }

  /**
   * Get the time of the last channel update.
   * @return {Date|null}
   */
  get updatedAt() {
    return this.data.updatedAt?.toDate() || null;
  }

  /**
   * Get the body of the last text message sent in the channel.
   * @return {string}
   */
  get lastMessage() {
    return this.data.lastMessage || {};
  }

  /**
   * Get the last message text, with markdown links collapsed to their label for plain-text previews.
   * @return {string}
   */
  get lastMessageText() {
    return stripMarkdownLinks(this.lastMessage.text || '');
  }

  /**
   * Get the time the last message was sent in the channel.
   * @return {Date|null}
   */
  get lastMessageAt() {
    return this.lastMessage.createdAt?.toDate() || null;
  }

  /**
   * Get the userId of the member who sent the last message in the channel.
   * @return {string}
   */
  get lastMessageBy() {
    return this.lastMessage.userId || '';
  }

  /**
   * Check if the channel is active.
   * @return {boolean}
   */
  get isActive() {
    return !!this.data.isActive;
  }

  /**
   * Check if the channel is archived.
   * @return {boolean}
   */
  get isArchived() {
    return !!this.data.isArchived;
  }

  /**
   * Check if the channel is hidden.
   * @return {boolean}
   */
  get isHidden() {
    return !!this.data.isHidden;
  }

  /**
   * Check if it's an admin chat channel or user chat channel.
   * @return {boolean}
   */
  get isAdmin() {
    return !!this.data.isAdmin;
  }

  /**
   * Get the members of the channel.
   * USER type channel will have only two members. coachId and userId.
   * @return {Array} an array of member ids
   */
  get memberIds() {
    return this.data.memberIds || [];
  }

  /**
   * Get the type of the channel. It can be either USER or GROUP.
   * @return {string}
   */
  get type() {
    return this.data.type || ChannelType.USER;
  }

  /**
   * Get the userId for USER type Channel.
   * @return {string}
   */
  get userId() {
    return this.data.userId || '';
  }

  /**
   * Get the coachId for USER type Channel.
   * @return {string}
   */
  get coachId() {
    return this.data.coachId || '';
  }

  /**
   * Get the member data of the channel.
   * @return {Object}
   */
  get memberData() {
    return this.data.memberData || {};
  }

  /**
   * Check if the user is online.
   * @return {boolean}
   */
  get isOnline() {
    return !!this.data.isOnline;
  }

  /**
   * AC who last interacted with the client.
   * @return {string}
   */
  get lastInteractionBy() {
    return this.data.lastInteractionBy;
  }

  /**
   * Id of the user who claimed the channel.
   * @return {string}
   */
  get claimedBy() {
    return this.data.claimedBy;
  }

  /**
   * Name of the user who claimed the channel.
   * @return {string}
   */
  get claimedByUsername() {
    return this.data.claimedByUsername;
  }

  /**
   * Time when the channel was claimed.
   * @return {Date}
   */
  get claimedAt() {
    return this.data.claimedAt?.toDate();
  }

  /**
   * Retrieves the source from which an AC claimed the channel.
   * @return {string|void}
   */
  get claimedFrom() {
    return this.data.claimedFrom;
  }

  /**
   * Check if the claim is still valid.
   * @return {boolean}
   */
  get isClaimValid() {
    return !!this.claimedAt && moment().diff(moment(this.claimedAt), 'hours') < CHANNEL_CLAIM_VALIDITY_HOURS;
  }

  /**
   * Check if the channel is enabled for AI friend.
   * @return {boolean}
   */
  get isAIFriendEnabled() {
    return !!this.data.isAIFriendEnabled;
  }

  /**
   * Channels with isPinned=true float to the top of the channel list.
   * @return {boolean}
   */
  get isPinned() {
    return !!this.data.isPinned;
  }

  // Methods
  /**
   * Unclaims a chat and if the `userId` matches the current claimee, and optonally updates
   * specified fields in the chat. The update is only performed if there are fields to update.
   *
   * @param {string} userId - The ID of the user attempting to unclaim the document.
   * @param {Object} [fields={}] - An object containing the fields to update in chat.
   * @param {boolean} unclaimCheckin - Indicates if the unclaim action was triggered from the check-in.
   *                                 This allows unclaiming even if the channel was claimed from chat.
   * @returns {Promise<void>} - A promise that resolves once the transaction completes successfully.
   */
  unclaimAndUpdateFields = async (userId, fields = {}, unclaimCheckin = false) => {
    const db = getFirebase().default.firestore();

    return db.runTransaction(async (transaction) => {
      const doc = await transaction.get(this.ref);

      if (doc.exists) {
        let updatedFields = fields;
        const claimedBySameUser = doc?.data()?.claimedBy === userId;
        const claimedFromChat = doc?.data()?.claimedFrom === ChannelClaimedFrom.CHAT;
        const claimedFromCheckin = doc?.data()?.claimedFrom === ChannelClaimedFrom.CHECKIN;

        // Unclaim if a userId is provided and it matches the current claimee
        if (claimedBySameUser && (claimedFromChat || (unclaimCheckin && claimedFromCheckin))) {
          updatedFields = {
            ...updatedFields,
            claimedBy: getFirebase().firestore.FieldValue.delete(),
            claimedByUsername: getFirebase().firestore.FieldValue.delete(),
            claimedAt: getFirebase().firestore.FieldValue.delete(),
            claimedFrom: getFirebase().firestore.FieldValue.delete(),
          };
        }

        // Perform an update if there are any fields to update
        if (Object.entries(updatedFields).length !== 0) {
          transaction.update(this.ref, updatedFields);
        }
      }
    });
  };

  archive(userId) {
    return this.unclaimAndUpdateFields(userId, {
      isArchived: true,
      isHidden: true,
    });
  }

  unarchive(userId) {
    return this.unclaimAndUpdateFields(userId, {
      isArchived: false,
      isHidden: false,
    });
  }

  hide(userId) {
    return this.unclaimAndUpdateFields(userId, { isHidden: true });
  }

  show(userId) {
    return this.unclaimAndUpdateFields(userId, {
      isHidden: false,
      isArchived: false,
    });
  }

  /**
   * Updates the channel to indicate that it has been claimed by a specific user.
   *
   * @param {string} claimedBy - Id of the user claiming the channel.
   * @param {string} claimedByUsername - Username of the user claiming the channel.
   * @param {string} claimedFrom - The source from which the user claimed the channel.
   * @returns {Promise<Object|null>} A promise that resolves to an object containing the claimee information.
   */
  claim(claimedBy, claimedByUsername, claimedFrom) {
    const db = getFirebase().default.firestore();

    // Run the update in a transaction to ensure that the channel is not claimed by someone else in the meantime.
    return db.runTransaction(async (transaction) => {
      const doc = await transaction.get(this.ref);

      if (!doc.exists) {
        return null;
      }

      const newClaimData = {
        claimedBy,
        claimedByUsername,
        claimedFrom,
        claimedAt: moment().toDate(),
      };

      const {
        claimedBy: currentClaimee,
        claimedAt,
      } = doc.data();

      // Claim the document if it is not already claimed or the claim has expired (more than 1 hour ago)
      if (!currentClaimee
        || (!!claimedAt && moment().diff(moment(claimedAt.toDate()), 'hours') >= CHANNEL_CLAIM_VALIDITY_HOURS)) {
        transaction.update(this.ref, newClaimData);

        // Return the new claim data if the document was successfully claimed
        return newClaimData;
      }

      // Return existing claimee data if the document is already claimed
      return doc.data();
    });
  }

  /**
   * Remove the claim in a channel.
   *
   * @param {string} userId - Id of the user unclaiming the channel.
   * @returns {Promise} Resolves when the fields are successfully updated.
   */
  unclaim(userId) {
    return this.unclaimAndUpdateFields(userId);
  }

  /**
   * Reclaims an already claimed chat channel
   *
   * @param {string} claimedBy - The ID of the user attempting to claim the document.
   * @param {string} claimedByUsername - The username of the user attempting to claim the document.
   * @param {string} claimedFrom - The source from which the user claimed the channel.
   * @returns {Promise} A promise that resolves when the fields are updated.
   */
  reclaim(claimedBy, claimedByUsername, claimedFrom) {
    const db = getFirebase().default.firestore();

    // Run the update in a transaction to ensure that the channel is not claimed by someone else in the meantime.
    return db.runTransaction(async (transaction) => {
      const doc = await transaction.get(this.ref);

      if (!doc.exists) {
        return null;
      }

      const reclaimData = {
        claimedBy,
        claimedByUsername,
        claimedFrom,
        claimedAt: moment().toDate(),
      };

      return transaction.update(this.ref, reclaimData);
    });
  }

  /**
   * Send a message to the current channel.
   * @param {object} options
   * @param {object} options.text The message to send.
   * @param {Array} options.attachments The attachments to send with the message.
   * @param {string} options.userId The id of the user sending the message.
   * @param {string} options.userName The name of the user sending the message.
   * @param {string} options.initiatedBy The logged in user. This is used by ACs sending messages on behalf of a coach.
   */
  sendMessage = async ({
    text = '',
    attachments = [],
    userId,
    userName,
    initiatedBy,
    ...extras
  }) => {
    // Send message to the current channel

    const db = getFirebase().default.firestore();
    const newMessageDocRef = db.collection(CollectionName.CHAT_MESSAGE).doc();

    // Build the doc object
    const newMessageContent = {
      channelId: this.id,
      text,
      userId,
      userName,
      initiatedBy,
      createdAt: getServerTimestamp(),
      attachments,
      ...extras,
    };

    return db.runTransaction(async (transaction) => transaction
      .set(newMessageDocRef, newMessageContent));
  }

  getMessageList = async (options = {}) => {
    const { startAfterDoc = null } = options;
    const channelMessageList = new Collection(CollectionName.CHAT_MESSAGE, {
      createDocument: (src, opts) => new ChatMessage(src.id, opts),
      query: (ref) => {
        let query = ref
          .where('channelId', '==', this.id)
          .orderBy('createdAt', 'desc')
          .limit(CHANNEL_MESSAGE_PAGE_SIZE);

        if (startAfterDoc) {
          query = query.startAfter(startAfterDoc.snapshot);
        }

        return query;
      },
    });

    await channelMessageList.fetch();
    return channelMessageList;
  }

  /**
   * This channel's most recent thread replies, newest first — its own query rather than a filter over
   * the loaded message window, which pagination caps: a reply buried under newer traffic could never
   * be announced, as we would not even hold its id.
   *
   * Needs the `channelId + isThreadReply + createdAt DESC` composite index. Safe without a backfill:
   * replies always carry `isThreadReply: true`, unlike the `== false` direction.
   * @param {Object} options Options
   * @param {Number} options.limit How many replies to hold
   * @returns {Promise<Collection>} Collection of recent thread replies
   */
  getRecentThreadReplies = async ({ limit }) => {
    const recentReplies = new Collection(CollectionName.CHAT_MESSAGE, {
      createDocument: (src, opts) => new ChatMessage(src.id, opts),
      query: (ref) => ref
        .where('channelId', '==', this.id)
        .where('isThreadReply', '==', true)
        .orderBy('createdAt', 'desc')
        .limit(limit),
    });

    await recentReplies.fetch();
    return recentReplies;
  }

  /**
   * The channel's thread replies newer than the coach's watermark — the unread inbox's source.
   *
   * Bounded by time rather than by count, so no unread thread can fall off the end however busy the
   * channel gets. `limit` is a seatbelt, not the bound; the caller announces it when it binds.
   * Served by the existing `channelId + isThreadReply + createdAt` composite index.
   * @param {Object} params Params
   * @param {Number} params.since Watermark in millis; replies at or below it count as read
   * @param {Number} params.limit Seatbelt ceiling on documents returned
   * @returns {Promise<Object>} Firestorter collection of thread replies, newest first
   */
  getUnreadThreadReplies = async ({ since, limit }) => {
    const unreadReplies = new Collection(CollectionName.CHAT_MESSAGE, {
      createDocument: (src, opts) => new ChatMessage(src.id, opts),
      query: (ref) => ref
        .where('channelId', '==', this.id)
        .where('isThreadReply', '==', true)
        .where('createdAt', '>', new Date(since))
        .orderBy('createdAt', 'desc')
        .limit(limit),
    });

    await unreadReplies.fetch();
    return unreadReplies;
  }

  getFlaggedMessageList = async () => {
    const flaggedMessageList = new Collection(CollectionName.CHAT_MESSAGE, {
      createDocument: (src, opts) => new ChatMessage(src.id, opts),
      query: (ref) => ref
        .where('channelId', '==', this.id)
        .where('isFlagged', '==', true),
    });

    await flaggedMessageList.fetch();
    return flaggedMessageList;
  }

  /**
   * Schedule a message to be sent to the current channel.
   * @param {object} options
   * @param {object} options.text The message to send.
   * @param {Array<object>} options.attachments The attachments to send with the message.
   * @param {string} options.userId The id of the user scheduling the message.
   * @param {string} options.initiatedBy The logged in user.
   *  This is used by ACs scheduling messages on behalf of a coach.
   * @param {Date} sendAt The datetime to schedule the message to be sent on.
   * @returns {Promise<void>}
   */
  scheduleMessage = async ({
    text = '',
    attachments = [],
    userId,
    initiatedBy,
    shouldForceSend,
    ...extras
  }, sendAt) => {
    const messageRequestCollection = new Collection(CollectionName.CHAT_MESSAGE_REQUEST);

    const newMessageRequestContent = {
      channelId: this.id,
      text,
      userId,
      initiatedBy,
      createdAt: getServerTimestamp(),
      attachments,
      sendAt,
      status: CHAT_MESSAGE_REQUEST_STATUS.CREATED,
      shouldForceSend,
      ...extras,
    };
    await messageRequestCollection.add(newMessageRequestContent);
  }

  /**
   * Gets all the messages scheduled by a user on the current channel.
   * @param {string} userId The id of the user whose scheduled messages will be fetched.
   * @returns {Promise<Array<ChatMessageRequest>>}
   */
  getScheduledMessages = async (userId) => {
    const channelScheduledMessages = new Collection(CollectionName.CHAT_MESSAGE_REQUEST, {
      createDocument: (src, opts) => new ChatMessageRequest(src, opts),
      query: (ref) => ref
        .where('userId', '==', userId)
        .where('channelId', '==', this.id)
        .where('status', '==', CHAT_MESSAGE_REQUEST_STATUS.CREATED)
        .orderBy('sendAt', 'asc'),
    });

    await channelScheduledMessages.fetch();
    return channelScheduledMessages;
  }

  /**
   * Gets all the pending scheduled messages by a user on the current channel.
   * @param {string} userId The id of the user whose pending scheduled messages will be fetched.
   * @returns {Promise<Array<ChatMessageRequest>>}
   */
  getPendingScheduledMessages = async (userId) => {
    const channelPendingScheduledMessages = new Collection(CollectionName.CHAT_MESSAGE_REQUEST, {
      createDocument: (src, opts) => new ChatMessageRequest(src, opts),
      query: (ref) => ref
        .where('userId', '==', userId)
        .where('channelId', '==', this.id)
        .where('status', '==', CHAT_MESSAGE_REQUEST_STATUS.PENDING)
        .orderBy('sendAt', 'asc'),
    });

    await channelPendingScheduledMessages.fetch();
    return channelPendingScheduledMessages;
  }

  static async getById(id) {
    const chatChannelDoc = new ChatChannel(id);
    await chatChannelDoc.init();
    return chatChannelDoc;
  }

  /**
   * get the channel for a user and coach combination
   * @param {string} userId The user id
   * @param {string} coachId The coach id
   * @returns {Promise} The channel document if found, null otherwise
   */
  static async getUserCoachChannel(userId, coachId) {
    const channelSnapshot = new Collection(() => `/${CollectionName.CHAT_CHANNEL}`, {
      createDocument: ({ id }, opts) => new ChatChannel(id, opts),
      query: (ref) => ref
        .where('userId', '==', userId)
        .where('coachId', '==', coachId)
        .limit(1),
    });

    await channelSnapshot.fetch();

    return channelSnapshot.docs[0];
  }

  /**
   * Archive multiple chats
   * @param {Object[]} docs
   */
  static async archiveChats(docs) {
    const batch = getFirebase().default.firestore().batch();

    docs.forEach((doc) => {
      batch.update(doc.ref, {
        isArchived: true,
        isHidden: true,
      });
    });

    await batch.commit();
  }

  /**
   * Archives all the active chats for a user
   * @param {string} userId
   * @param {string} [selectedPodId]
   */
  static async archiveAllChats(userId, selectedPodId = '') {
    const chatChannelList = new Collection(CollectionName.CHAT_CHANNEL, {
      createDocument: (src, opts) => new ChatChannel(src.id, opts),
      query: (ref) => {
        const query = ref
          .where('memberIds', 'array-contains', userId)
          .where('isArchived', '==', false)
          .where('isHidden', '==', false);

        return applyPodFilter(query, selectedPodId);
      },
    });

    const collection = await chatChannelList.fetch();
    await ChatChannel.archiveChats(collection.docs);
  }

  /**
   * Get the channel list
   * @param {string} userId
   * @param {Object} options
   * @param {ChatChannel | null} options.startAfterDoc
   * @param {string} options.bucket
   * @param {boolean} options.isRecentFirst
   * @param {string} options.searchTerm
   * @param {string} options.selectedPodId
   * @returns {Collection}
   */
  static async getChannelList(userId, options = {}) {
    const {
      startAfterDoc = null,
      bucket,
      isRecentFirst = true,
      searchTerm = '',
      selectedPodId = '',
    } = options;

    const orderBy = isRecentFirst ? ChannelOrderBy.DESC : ChannelOrderBy.ASC;

    const chatChannelList = new Collection(CollectionName.CHAT_CHANNEL, {
      createDocument: (src, opts) => new ChatChannel(src.id, opts),
      query: (ref) => {
        let query = ref
          .where('memberIds', 'array-contains', userId);

        if (searchTerm) {
          const formattedSearchTerm = searchTerm.toLowerCase();
          query = query
            .where('searchName', '>=', formattedSearchTerm)
            .where('searchName', '<=', `${formattedSearchTerm}\uf8ff`)
            .orderBy('searchName');
        }

        if (bucket === ChannelBuckets.ARCHIVED) {
          query = query.where('isArchived', '==', true);
        } else if (bucket === ChannelBuckets.ACTIVE) {
          query = query.where('isArchived', '==', false).where('isHidden', '==', false);
        } else if (bucket === ChannelBuckets.DRAFT) {
          query = query.where('isPending', '==', true);
        } else if (bucket === ChannelBuckets.AI_FRIEND) {
          query = query.where('isAIFriendEnabled', '==', true);
        }

        query = applyPodFilter(query, selectedPodId);

        query = query
          .orderBy('lastMessage.createdAt', orderBy)
          .limit(CHANNEL_LIST_PAGE_SIZE);

        if (startAfterDoc) {
          const startAtParams = [];

          // When searching, we add the search term to the startAtParams so that we can filter the list correctly.
          if (searchTerm) {
            startAtParams.push(startAfterDoc.searchName);
          }
          // We are always sorting by lastMessage.createdAt (in asc or desc order), so we can safely add it to the list.
          startAtParams.push(startAfterDoc.lastMessage.createdAt);

          // Paginate the query using the same number of parameters as the orderBy entries.
          query = query.startAfter(...startAtParams);
        }
        return query;
      },
    });

    await chatChannelList.fetch();
    return chatChannelList;
  }

  static async getClaimedChannelList(userId) {
    const timeThresholdForValidClaim = moment().subtract(CHANNEL_CLAIM_VALIDITY_HOURS, 'hours').toDate();

    const claimChannels = new Collection(CollectionName.CHAT_CHANNEL, {
      createDocument: (src, opts) => new ChatChannel(src.id, opts),
      query: (ref) => ref
        .where('memberIds', 'array-contains', userId)
        .where('claimedAt', '>=', timeThresholdForValidClaim),
    });

    await claimChannels.fetch();
    return claimChannels;
  }

  static async getPendingChannelList(userId, selectedPodId = '') {
    const chatChannelList = new Collection(CollectionName.CHAT_CHANNEL, {
      createDocument: (src, opts) => new ChatChannel(src.id, opts),
      query: (ref) => {
        const query = ref
          .where('isPending', '==', true)
          .where('memberIds', 'array-contains', userId);

        return applyPodFilter(query, selectedPodId);
      },
    });

    await chatChannelList.fetch();
    return chatChannelList;
  }
}

export default ChatChannel;
export {
  ChannelType,
  CHANNEL_LIST_PAGE_SIZE,
  CHANNEL_MESSAGE_PAGE_SIZE,
  THREAD_REPLY_PAGE_SIZE,
  UNSEEN_THREAD_REPLY_LIMIT,
  UNREAD_THREAD_SEATBELT,
  ChannelClaimedFrom,
};
